import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Users, Mail, CreditCard, BarChart3, Database, Loader2, RefreshCw, FileText, Zap, Clock, Activity, PieChart, AlertTriangle } from "lucide-react";

export default function Admin() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [emails, setEmails] = useState([]);
  const [payments, setPayments] = useState([]);
  const [usage, setUsage] = useState([]);
  const [corpus, setCorpus] = useState({ results: [], total: 0 });
  const [corpusStats, setCorpusStats] = useState(null);
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [reembedBusy, setReembedBusy] = useState(false);
  const [corpusQ, setCorpusQ] = useState("");
  const [jobs, setJobs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docsMeta, setDocsMeta] = useState({ total: 0, skip: 0, limit: 25, has_more: false });
  const [docsQ, setDocsQ] = useState("");
  const [docsStatus, setDocsStatus] = useState("");
  const [docsStorage, setDocsStorage] = useState("");
  const [rechunkBusy, setRechunkBusy] = useState(null);
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditMeta, setAuditMeta] = useState({ total: 0, skip: 0, limit: 25, has_more: false, types: [] });
  const [auditType, setAuditType] = useState("");
  const [auditSince, setAuditSince] = useState("");
  const [auditUntil, setAuditUntil] = useState("");
  // Coverage analytics
  const [coverage, setCoverage] = useState(null);
  const [trends, setTrends] = useState(null);
  const [alerts, setAlerts] = useState(null);
  // Core topics editor
  const [topicsList, setTopicsList] = useState([]);
  const [topicsInput, setTopicsInput] = useState("");
  const [topicsDirty, setTopicsDirty] = useState(false);
  const [topicsBusy, setTopicsBusy] = useState(false);
  const [gapBusy, setGapBusy] = useState(null);

  const loadJobs = async () => {
    try {
      const r = await api.get("/admin/corpus/jobs");
      setJobs(r.data.jobs || []);
    } catch (e) {}
  };

  const loadCorpus = async (q = "") => {
    const res = await api.get(`/corpus?limit=50${q ? `&q=${encodeURIComponent(q)}` : ""}`);
    setCorpus(res.data);
    const s = await api.get("/admin/corpus/stats");
    setCorpusStats(s.data);
    loadJobs();
  };

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data.users));
    api.get("/admin/emails").then((r) => setEmails(r.data.emails));
    api.get("/admin/subscriptions").then((r) => setPayments(r.data.payments));
    api.get("/admin/usage").then((r) => setUsage(r.data.events));
    loadCorpus();
  }, []);

  const triggerScrape = async (source) => {
    setScrapeBusy(true);
    try {
      const r = await api.post(`/admin/corpus/scrape?source=${source}&limit=15`);
      toast.success(`Job queued: ${r.data.job_id.slice(0, 8)}`);
      loadJobs();
    } catch (e) {
      toast.error("Could not queue job");
    } finally { setScrapeBusy(false); }
  };

  // Auto-refresh jobs while any are queued/running
  useEffect(() => {
    if (tab !== "corpus") return;
    const hasActive = jobs.some((j) => j.status === "queued" || j.status === "running");
    if (!hasActive) return;
    const tick = setInterval(() => { loadJobs(); loadCorpus(corpusQ); }, 3000);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [tab, jobs]);

  const reseed = async () => {
    setScrapeBusy(true);
    try {
      const r = await api.post(`/admin/corpus/seed`);
      toast.success(`Seeded: ${r.data.inserted} new entries (total ${r.data.total})`);
      loadCorpus(corpusQ);
    } catch (e) { toast.error("Seed failed"); }
    finally { setScrapeBusy(false); }
  };

  const reembed = async () => {
    setReembedBusy(true);
    try {
      const r = await api.post(`/admin/corpus/reembed`);
      toast.success(`Re-embedded ${r.data.embedded}/${r.data.total} entries`);
    } catch (e) { toast.error("Re-embed failed"); }
    finally { setReembedBusy(false); }
  };

  const loadDocuments = async (skip = 0) => {
    try {
      const params = new URLSearchParams({ limit: String(docsMeta.limit), skip: String(skip) });
      if (docsQ) params.set("q", docsQ);
      if (docsStatus) params.set("chunk_status", docsStatus);
      if (docsStorage) params.set("storage_backend", docsStorage);
      const r = await api.get(`/admin/documents?${params.toString()}`);
      setDocuments(r.data.documents || []);
      setDocsMeta({
        total: r.data.total || 0,
        skip: r.data.skip || 0,
        limit: r.data.limit || 25,
        has_more: !!r.data.has_more,
      });
    } catch (e) { /* noop */ }
  };

  const rechunkDocument = async (docId) => {
    setRechunkBusy(docId);
    try {
      const r = await api.post(`/admin/documents/${docId}/rechunk`);
      toast.success(`Re-chunk queued (${r.data.runner})`);
      setTimeout(() => loadDocuments(docsMeta.skip), 2500);
    } catch (e) { toast.error("Re-chunk failed"); }
    finally { setRechunkBusy(null); }
  };

  useEffect(() => {
    if (tab === "documents") loadDocuments(0);
    if (tab === "activity") loadAuditLogs(0);
    if (tab === "corpus") loadCoverage();
    // eslint-disable-next-line
  }, [tab]);

  const loadAuditLogs = async (skip = 0) => {
    try {
      const params = new URLSearchParams({ limit: String(auditMeta.limit), skip: String(skip) });
      if (auditType) params.set("type", auditType);
      if (auditSince) params.set("since", auditSince);
      if (auditUntil) params.set("until", auditUntil);
      const r = await api.get(`/admin/audit-logs?${params.toString()}`);
      setAuditLogs(r.data.logs || []);
      setAuditMeta({
        total: r.data.total || 0,
        skip: r.data.skip || 0,
        limit: r.data.limit || 25,
        has_more: !!r.data.has_more,
        types: r.data.types || [],
      });
    } catch (e) { /* noop */ }
  };

  const loadCoverage = async () => {
    try {
      const [covRes, trRes, alRes, topRes] = await Promise.all([
        api.get("/admin/corpus/coverage"),
        api.get("/admin/corpus/trends?weeks=12"),
        api.get("/admin/notifications"),
        api.get("/admin/config/core-topics"),
      ]);
      setCoverage(covRes.data);
      setTrends(trRes.data);
      setAlerts(alRes.data);
      setTopicsList(topRes.data.topics || []);
      setTopicsDirty(false);
    } catch (e) { /* noop */ }
  };

  const saveTopics = async () => {
    setTopicsBusy(true);
    try {
      const r = await api.put("/admin/config/core-topics", { topics: topicsList });
      toast.success(`Saved ${r.data.count} core topics`);
      setTopicsList(r.data.topics || []);
      setTopicsDirty(false);
      loadCoverage();
    } catch (e) { toast.error("Could not save topics"); }
    finally { setTopicsBusy(false); }
  };

  const addTopic = () => {
    const t = (topicsInput || "").trim();
    if (!t) return;
    if (topicsList.map((x) => x.toLowerCase()).includes(t.toLowerCase())) {
      toast.error("Topic already exists");
      return;
    }
    setTopicsList([...topicsList, t]);
    setTopicsInput("");
    setTopicsDirty(true);
  };

  const removeTopic = (topic) => {
    setTopicsList(topicsList.filter((x) => x !== topic));
    setTopicsDirty(true);
  };

  const triggerGapScrape = async (topic) => {
    setGapBusy(topic);
    try {
      const r = await api.post("/admin/corpus/gap-scrape", { topic, limit: 5 });
      toast.success(`Queued scrape for "${topic}" (${r.data.runner})`);
    } catch (e) { toast.error("Could not queue scrape"); }
    finally { setGapBusy(null); }
  };

  return (
    <DashboardLayout>
      <div className="kicker">Admin</div>
      <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl text-slate-900">{t("admin.title")}</h1>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats && [
          { label: t("admin.users"), value: stats.users, Icon: Users },
          { label: t("admin.matters"), value: stats.matters, Icon: BarChart3 },
          { label: t("admin.reports"), value: stats.reports, Icon: BarChart3 },
          { label: t("admin.subs"), value: stats.active_subscriptions, Icon: CreditCard },
          { label: t("admin.mrr"), value: `€${stats.mrr_eur}`, Icon: CreditCard },
        ].map((s, i) => (
          <div key={i} className="card-premium p-5" data-testid={`admin-stat-${i}`}>
            <div className="flex items-center justify-between">
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">{s.label}</div>
              <s.Icon className="h-4 w-4 text-slate-400" />
            </div>
            <div className="mt-3 font-display font-bold text-3xl text-slate-900">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex gap-2 border-b border-slate-200 overflow-x-auto">
        {[
          { k: "users", label: t("admin.tabsUsers") },
          { k: "emails", label: t("admin.tabsEmails") },
          { k: "payments", label: t("admin.tabsPayments") },
          { k: "usage", label: t("admin.tabsUsage") },
          { k: "corpus", label: "Corpus" },
          { k: "documents", label: "Documents" },
          { k: "activity", label: "Activity" },
        ].map((tb) => (
          <button key={tb.k} onClick={() => setTab(tb.k)}
                  data-testid={`admin-tab-${tb.k}`}
                  className={`px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    tab === tb.k ? "border-[#0A192F] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}>
            {tb.label}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        {tab === "users" && (
          <div className="overflow-x-auto -mx-2 px-2"><table className="w-full text-sm min-w-[640px]" data-testid="admin-users-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Plan</th>
                <th className="text-left py-2 px-3">Role</th>
                <th className="text-left py-2 px-3">Verified</th>
                <th className="text-left py-2 px-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100">
                  <td className="py-2 px-3">{u.email}</td>
                  <td className="py-2 px-3">{u.full_name}</td>
                  <td className="py-2 px-3">{u.plan || "free"}</td>
                  <td className="py-2 px-3">{u.role}</td>
                  <td className="py-2 px-3">{u.email_verified ? "✓" : "—"}</td>
                  <td className="py-2 px-3 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        {tab === "emails" && (
          <div className="overflow-x-auto -mx-2 px-2"><table className="w-full text-sm min-w-[640px]" data-testid="admin-emails-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3">Recipient</th>
                <th className="text-left py-2 px-3">Template</th>
                <th className="text-left py-2 px-3">Lang</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Time</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((e) => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="py-2 px-3">{e.recipient}</td>
                  <td className="py-2 px-3">{e.template}</td>
                  <td className="py-2 px-3">{e.language}</td>
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      e.status === "sent" ? "bg-emerald-100 text-emerald-700" :
                      e.status === "simulated" ? "bg-slate-100 text-slate-600" :
                      e.status === "failed" ? "bg-red-100 text-red-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>{e.status}</span>
                  </td>
                  <td className="py-2 px-3 text-slate-500">{new Date(e.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        {tab === "payments" && (
          <div className="overflow-x-auto -mx-2 px-2"><table className="w-full text-sm min-w-[640px]" data-testid="admin-payments-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3">User</th>
                <th className="text-left py-2 px-3">Plan</th>
                <th className="text-left py-2 px-3">Amount</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.session_id} className="border-b border-slate-100">
                  <td className="py-2 px-3">{p.user_email}</td>
                  <td className="py-2 px-3">{p.plan_name} · {p.cadence}</td>
                  <td className="py-2 px-3">{p.amount} {p.currency?.toUpperCase()}</td>
                  <td className="py-2 px-3">{p.payment_status}</td>
                  <td className="py-2 px-3 text-slate-500">{new Date(p.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        {tab === "usage" && (
          <div className="overflow-x-auto -mx-2 px-2"><table className="w-full text-sm min-w-[640px]" data-testid="admin-usage-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3">User</th>
                <th className="text-left py-2 px-3">Event</th>
                <th className="text-left py-2 px-3">Matter</th>
                <th className="text-left py-2 px-3">Time</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((e) => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="py-2 px-3">{e.user_id}</td>
                  <td className="py-2 px-3">{e.event}</td>
                  <td className="py-2 px-3">{e.matter_id || "—"}</td>
                  <td className="py-2 px-3 text-slate-500">{new Date(e.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
        {tab === "corpus" && (
          <div data-testid="admin-corpus-tab">
            {corpusStats && (
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="card-premium p-5">
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">Corpus Total</div>
                  <div className="mt-2 font-display font-bold text-3xl text-slate-900">{corpusStats.total}</div>
                </div>
                <div className="card-premium p-5">
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">By Type</div>
                  <div className="mt-2 text-sm text-slate-700 space-y-1">
                    {Object.entries(corpusStats.by_type).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span className="font-mono">{v}</span></div>
                    ))}
                  </div>
                </div>
                <div className="card-premium p-5">
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">By Source</div>
                  <div className="mt-2 text-sm text-slate-700 space-y-1">
                    {Object.entries(corpusStats.by_source).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span>{k}</span><span className="font-mono">{v}</span></div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {coverage && (
              <div className="card-premium p-5 mb-6" data-testid="corpus-coverage">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <PieChart className="h-4 w-4 text-[#8B1E3F]" />
                    <span className="text-sm font-medium text-slate-800">Coverage Analytics</span>
                    <span className="text-xs text-slate-500">
                      {coverage.topics_with_entries} topics · {coverage.core_topics_total} core
                    </span>
                  </div>
                  <button onClick={loadCoverage} className="text-xs text-[#0A192F] hover:underline" data-testid="coverage-refresh">
                    <RefreshCw className="h-3 w-3 inline -mt-0.5 mr-1" /> Refresh
                  </button>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mt-5">
                  {/* Top topics (heatmap-style bar) */}
                  <div className="md:col-span-2">
                    <div className="kicker !text-slate-500 mb-2">Top topics</div>
                    <div className="space-y-1.5" data-testid="coverage-top-topics">
                      {(coverage.top_topics || []).map((t) => {
                        const max = coverage.top_topics[0]?.count || 1;
                        const pct = Math.round((t.count / max) * 100);
                        return (
                          <div key={t.topic} className="flex items-center gap-3">
                            <span className="text-xs text-slate-700 capitalize w-36 truncate">{t.topic}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                              <div className="h-full bg-[#8B1E3F] rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-mono text-slate-500 w-8 text-right">{t.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Gaps — clickable pills to trigger targeted scrape */}
                  <div>
                    <div className="kicker !text-slate-500 mb-2 flex items-center gap-1.5">
                      <AlertTriangle className="h-3 w-3 text-amber-600" /> Gaps · {(coverage.gaps || []).length}
                    </div>
                    <div className="space-y-1.5" data-testid="coverage-gaps">
                      {(coverage.gaps || []).map((g) => (
                        <button
                          key={g.topic}
                          onClick={() => triggerGapScrape(g.topic)}
                          disabled={gapBusy === g.topic}
                          title={`Trigger scrape for "${g.topic}"`}
                          className={`w-full flex items-center justify-between text-xs px-2.5 py-1.5 rounded-full border transition hover:shadow-sm cursor-pointer disabled:opacity-60 ${
                             g.severity === "missing"
                               ? "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
                               : "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
                           }`}
                          data-testid={`gap-scrape-${g.topic.toLowerCase().replace(/\s+/g,'-')}`}>
                          <span className="capitalize flex items-center gap-1.5">
                            {gapBusy === g.topic ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                            {g.topic}
                          </span>
                          <span className="font-mono">{g.count}</span>
                        </button>
                      ))}
                      {(coverage.gaps || []).length === 0 && (
                        <div className="text-xs text-slate-400">All core topics covered ✓</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Source freshness */}
                <div className="mt-5">
                  <div className="kicker !text-slate-500 mb-2">Source freshness</div>
                  <div className="grid sm:grid-cols-3 gap-2" data-testid="coverage-freshness">
                    {(coverage.source_freshness || []).map((s) => {
                      const color = {
                        fresh:   "bg-emerald-50 border-emerald-200 text-emerald-700",
                        aging:   "bg-amber-50 border-amber-200 text-amber-800",
                        stale:   "bg-rose-50 border-rose-200 text-rose-700",
                        never:   "bg-slate-100 border-slate-200 text-slate-500",
                        unknown: "bg-slate-100 border-slate-200 text-slate-500",
                      }[s.status] || "bg-slate-100 border-slate-200 text-slate-500";
                      return (
                        <div key={s.source} className={`rounded-lg border px-3 py-2 text-xs flex items-center justify-between ${color}`}>
                          <span className="font-medium">{s.source}</span>
                          <span className="font-mono">
                            {s.days_stale === null ? "—" : `${s.days_stale}d`}
                            {" · "}
                            {s.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Alerts banner */}
                {alerts && alerts.has_notifications && (
                  <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="coverage-alerts">
                    <div className="kicker !text-amber-700 mb-2 flex items-center gap-1.5">
                      <AlertTriangle className="h-3 w-3" /> Alerts · {alerts.total}
                    </div>
                    <ul className="space-y-1">
                      {(alerts.alerts || []).map((a) => (
                        <li key={a.code} data-testid={`alert-${a.code}`}
                            className={`text-xs flex items-center gap-2 ${a.severity === "error" ? "text-rose-700" : "text-amber-800"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${a.severity === "error" ? "bg-rose-500" : "bg-amber-500"}`} />
                          {a.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Growth sparkline — dual line: cumulative (area) + weekly-added bars */}
                {trends && (trends.buckets || []).length > 0 && (() => {
                  const buckets = trends.buckets || [];
                  const cumulatives = buckets.map((b) => b.cumulative);
                  const addeds = buckets.map((b) => b.added);
                  const maxC = Math.max(...cumulatives, 1);
                  const maxA = Math.max(...addeds, 1);
                  const w = 640, h = 80, pad = 6;
                  const stepX = buckets.length > 1 ? (w - pad * 2) / (buckets.length - 1) : 0;
                  const cumPoints = cumulatives.map((v, i) => {
                    const x = pad + i * stepX;
                    const y = h - pad - ((v / maxC) * (h - pad * 2));
                    return `${x.toFixed(1)},${y.toFixed(1)}`;
                  }).join(" ");
                  const lastAdded = buckets[buckets.length - 1]?.added || 0;
                  const totalAdded = buckets.reduce((s, b) => s + b.added, 0);
                  // Weekly-added bars width
                  const barW = stepX > 0 ? Math.max(3, stepX * 0.55) : 6;
                  return (
                    <div className="mt-5" data-testid="coverage-trends">
                      <div className="kicker !text-slate-500 mb-2 flex items-center justify-between flex-wrap gap-2">
                        <span>Growth · last {buckets.length} weeks</span>
                        <span className="text-xs font-mono text-slate-500 flex items-center gap-3">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block h-2 w-3 rounded-sm bg-[#8B1E3F]" /> cumulative {cumulatives[cumulatives.length - 1] || 0}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block h-2 w-3 rounded-sm bg-[#0A192F]/50" /> weekly +{lastAdded}
                          </span>
                          <span>· total +{totalAdded}</span>
                        </span>
                      </div>
                      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none" data-testid="coverage-trends-svg">
                        {/* Weekly-added bars (back layer, navy translucent) */}
                        {buckets.map((b, i) => {
                          const x = pad + i * stepX - barW / 2;
                          const bh = b.added > 0 ? Math.max(1.5, (b.added / maxA) * (h - pad * 2)) : 0;
                          const y = h - pad - bh;
                          if (bh === 0) return null;
                          return (
                            <rect key={`bar-${i}`} x={x.toFixed(1)} y={y.toFixed(1)}
                                  width={barW.toFixed(1)} height={bh.toFixed(1)}
                                  fill="rgba(11,59,111,0.42)" rx="1.5"
                                  data-testid={`coverage-bar-${i}`}>
                              <title>+{b.added} added · week of {b.week_start}</title>
                            </rect>
                          );
                        })}
                        {/* Cumulative area + polyline (teal, foreground) */}
                        <polyline fill="rgba(15,122,133,0.14)" stroke="none"
                                  points={`${pad},${h - pad} ${cumPoints} ${pad + (buckets.length - 1) * stepX},${h - pad}`} />
                        <polyline fill="none" stroke="#8B1E3F" strokeWidth="2"
                                  points={cumPoints} data-testid="coverage-cum-line" />
                        {buckets.map((b, i) => {
                          const x = pad + i * stepX;
                          const y = h - pad - ((b.cumulative / maxC) * (h - pad * 2));
                          return <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r="2" fill="#8B1E3F" />;
                        })}
                      </svg>
                      <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-1">
                        <span>{buckets[0]?.week_start}</span>
                        <span>{buckets[buckets.length - 1]?.week_start}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Core topics editor */}
                <div className="mt-5 border-t border-slate-100 pt-5" data-testid="core-topics-editor">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="kicker !text-slate-500">Core topics ({topicsList.length})</div>
                    {topicsDirty && (
                      <button onClick={saveTopics} disabled={topicsBusy}
                              className="btn-primary rounded-full px-3 py-1 text-xs disabled:opacity-60"
                              data-testid="core-topics-save">
                        {topicsBusy ? <Loader2 className="h-3 w-3 animate-spin inline -mt-0.5" /> : null} Save
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="core-topics-list">
                    {topicsList.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
                            data-testid={`core-topic-${t.toLowerCase().replace(/\s+/g,'-')}`}>
                        <span className="capitalize">{t}</span>
                        <button onClick={() => removeTopic(t)}
                                className="text-slate-400 hover:text-rose-600 font-bold"
                                title="Remove"
                                data-testid={`core-topic-remove-${t.toLowerCase().replace(/\s+/g,'-')}`}>×</button>
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input value={topicsInput}
                           onChange={(e) => setTopicsInput(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") addTopic(); }}
                           placeholder="Add a core topic…"
                           className="rounded-full border border-slate-300 bg-white px-4 py-1.5 text-sm w-full max-w-sm focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                           data-testid="core-topic-input" />
                    <button onClick={addTopic}
                            className="rounded-full border border-[#0A192F] text-[#0A192F] px-3 py-1.5 text-xs hover:bg-[#0A192F]/5"
                            data-testid="core-topic-add">Add</button>
                  </div>
                </div>
              </div>
            )}

            <div className="card-premium p-5 mb-6">
              <div className="flex items-center gap-2 flex-wrap">
                <Database className="h-4 w-4 text-[#8B1E3F]" />
                <span className="text-sm font-medium text-slate-800">Sources</span>
                <button onClick={reseed} disabled={scrapeBusy}
                        className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid="corpus-reseed">
                  <RefreshCw className="h-3.5 w-3.5" /> Re-seed curated
                </button>
                {(corpusStats?.available_scrapers || []).map((s) => (
                  <button key={s} onClick={() => triggerScrape(s)} disabled={scrapeBusy}
                          className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-60"
                          data-testid={`corpus-scrape-${s}`}>
                    {scrapeBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
                    Scrape {s}
                  </button>
                ))}
                <button onClick={() => triggerScrape("all")} disabled={scrapeBusy}
                        className="btn-primary rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid="corpus-scrape-all">
                  Scrape all
                </button>
                <button onClick={reembed} disabled={reembedBusy}
                        className="rounded-full border border-[#8B1E3F] text-[#8B1E3F] px-3 py-1.5 text-xs hover:bg-[#8B1E3F]/5 inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid="corpus-reembed">
                  {reembedBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                  Re-embed vectors
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Scrapers run admin-triggered with rate-limit (1 req / 2s). Respect ToS of source sites. Jobs run in the background — track progress below. Nightly auto-scrape runs at 03:00 UTC (look for <span className="font-mono text-[#8B1E3F]">cron-*</span> jobs).
              </p>
            </div>

            {jobs.length > 0 && (
              <div className="card-premium p-5 mb-6" data-testid="scrape-jobs-panel">
                <div className="flex items-center justify-between">
                  <div className="kicker !text-slate-500">Scrape jobs</div>
                  <button onClick={loadJobs} className="text-xs text-[#0A192F] hover:underline" data-testid="jobs-refresh">Refresh</button>
                </div>
                <div className="mt-4 space-y-2">
                  {jobs.slice(0, 8).map((j) => {
                    const pill = {
                      queued:    "bg-slate-100 text-slate-600",
                      running:   "bg-amber-100 text-amber-800 animate-pulse",
                      completed: "bg-emerald-100 text-emerald-700",
                      partial:   "bg-amber-100 text-amber-800",
                      failed:    "bg-red-100 text-red-700",
                    }[j.status] || "bg-slate-100 text-slate-600";
                    const dur = j.started_at && j.completed_at
                      ? Math.max(0, Math.round((new Date(j.completed_at) - new Date(j.started_at)) / 1000))
                      : null;
                    return (
                      <div key={j.id} className={`rounded-lg border ${j.source === "cron" ? "border-[#8B1E3F]/40 bg-[#8B1E3F]/5" : "border-slate-200 bg-white"} p-3 flex items-center gap-3 flex-wrap`} data-testid={`scrape-job-${j.id}`}>
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${pill}`}>{j.status}</span>
                        {j.source === "cron" && (
                          <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#8B1E3F] text-white inline-flex items-center gap-1" data-testid={`cron-badge-${j.id}`}>
                            <Clock className="h-3 w-3" /> CRON
                          </span>
                        )}
                        <span className="font-mono text-xs text-slate-500">{j.id.slice(0, 8)}</span>
                        <span className="text-sm text-slate-700">sources: {(j.sources || []).join(", ")}</span>
                        <span className="text-xs text-slate-500">limit {j.limit}</span>
                        <span className="text-xs text-slate-500">+{j.inserted_or_updated || 0}</span>
                        {Object.keys(j.per_source || {}).length > 0 && (
                          <span className="text-xs text-slate-500">
                            ({Object.entries(j.per_source).map(([k, v]) => `${k}:${v}`).join(", ")})
                          </span>
                        )}
                        {dur !== null && <span className="text-xs text-slate-400">{dur}s</span>}
                        {Object.keys(j.errors || {}).length > 0 && (
                          <span className="text-xs text-red-600">errors: {Object.keys(j.errors).join(", ")}</span>
                        )}
                        <span className="ml-auto text-xs text-slate-400">{new Date(j.created_at).toLocaleTimeString()}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mb-4 flex gap-2">
              <input value={corpusQ} onChange={(e) => setCorpusQ(e.target.value)}
                     placeholder="Search corpus…"
                     className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm w-full sm:max-w-sm focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                     data-testid="corpus-search" />
              <button onClick={() => loadCorpus(corpusQ)} className="btn-primary rounded-full px-4 py-2 text-sm" data-testid="corpus-search-btn">Search</button>
            </div>
            <div className="overflow-x-auto -mx-2 px-2"><table className="w-full text-sm min-w-[640px]" data-testid="admin-corpus-table">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3">Reference</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-left py-2 px-3">Topics</th>
                  <th className="text-left py-2 px-3">Source</th>
                  <th className="text-left py-2 px-3">Link</th>
                </tr>
              </thead>
              <tbody>
                {(corpus.results || []).map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="py-2 px-3">{r.reference}</td>
                    <td className="py-2 px-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{r.type}</span></td>
                    <td className="py-2 px-3 text-slate-600">{(r.topics || []).slice(0, 3).join(", ")}</td>
                    <td className="py-2 px-3 text-slate-500">{r.source || "—"}</td>
                    <td className="py-2 px-3"><a href={r.source_url} target="_blank" rel="noreferrer" className="text-[#0A192F] underline">open</a></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <div className="mt-3 text-xs text-slate-500">{corpus.total} entries</div>
          </div>
        )}
        {tab === "documents" && (
          <div data-testid="admin-documents-tab">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="kicker !text-slate-500">Recent user documents · manual re-chunk</div>
              <button onClick={() => loadDocuments(0)} className="text-xs text-[#0A192F] hover:underline" data-testid="docs-refresh">
                <RefreshCw className="h-3 w-3 inline -mt-0.5 mr-1" /> Refresh
              </button>
            </div>
            <div className="mb-4 flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Filename</label>
                <input value={docsQ} onChange={(e) => setDocsQ(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") loadDocuments(0); }}
                       placeholder="Search filename…"
                       className="mt-1 rounded-full border border-slate-300 bg-white px-4 py-1.5 text-sm w-full focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                       data-testid="docs-filter-q" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Status</label>
                <select value={docsStatus} onChange={(e) => { setDocsStatus(e.target.value); }}
                        className="mt-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                        data-testid="docs-filter-status">
                  <option value="">Any</option>
                  <option value="ready">ready</option>
                  <option value="processing">processing</option>
                  <option value="queued">queued</option>
                  <option value="empty">empty</option>
                  <option value="failed">failed</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Storage</label>
                <select value={docsStorage} onChange={(e) => { setDocsStorage(e.target.value); }}
                        className="mt-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                        data-testid="docs-filter-storage">
                  <option value="">Any</option>
                  <option value="object_storage">object_storage</option>
                  <option value="mongo">mongo</option>
                </select>
              </div>
              <button onClick={() => loadDocuments(0)}
                      className="btn-primary rounded-full px-4 py-1.5 text-sm"
                      data-testid="docs-apply-filters">Apply</button>
              {(docsQ || docsStatus || docsStorage) && (
                <button onClick={() => { setDocsQ(""); setDocsStatus(""); setDocsStorage(""); setTimeout(() => loadDocuments(0), 0); }}
                        className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 text-slate-600"
                        data-testid="docs-clear-filters">Clear</button>
              )}
            </div>
            <div className="overflow-x-auto -mx-2 px-2"><table className="w-full text-sm min-w-[640px]" data-testid="admin-documents-table">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3">Filename</th>
                  <th className="text-left py-2 px-3">Matter</th>
                  <th className="text-left py-2 px-3">Owner</th>
                  <th className="text-left py-2 px-3">Chunks</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Storage</th>
                  <th className="text-left py-2 px-3">Created</th>
                  <th className="text-right py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => {
                  const st = d.chunk_status || "—";
                  const stColor = {
                    ready:      "bg-emerald-100 text-emerald-700",
                    processing: "bg-amber-100 text-amber-800 animate-pulse",
                    queued:     "bg-slate-100 text-slate-600",
                    empty:      "bg-slate-100 text-slate-500",
                    failed:     "bg-red-100 text-red-700",
                  }[st] || "bg-slate-100 text-slate-500";
                  return (
                    <tr key={d.id} className="border-b border-slate-100" data-testid={`admin-doc-row-${d.id}`}>
                      <td className="py-2 px-3 text-slate-800 flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate max-w-[220px]" title={d.filename}>{d.filename}</span>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-slate-500">{(d.matter_id || "").slice(0, 8)}</td>
                      <td className="py-2 px-3 font-mono text-xs text-slate-500">{(d.user_id || "").slice(0, 8)}</td>
                      <td className="py-2 px-3 font-mono">{d.chunk_count ?? 0}</td>
                      <td className="py-2 px-3">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${stColor}`}>{st}</span>
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">{d.storage_backend || "mongo"}</td>
                      <td className="py-2 px-3 text-xs text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                      <td className="py-2 px-3 text-right">
                        <button onClick={() => rechunkDocument(d.id)} disabled={rechunkBusy === d.id}
                                className="rounded-full border border-slate-200 px-2.5 py-1 text-xs hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-60"
                                data-testid={`rechunk-${d.id}`}>
                          {rechunkBusy === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          Re-chunk
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {documents.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-sm text-slate-400">No documents match your filters</td></tr>
                )}
              </tbody>
            </table></div>
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500" data-testid="docs-pagination">
              <span>
                Showing {documents.length === 0 ? 0 : docsMeta.skip + 1}–{docsMeta.skip + documents.length} of {docsMeta.total}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => loadDocuments(Math.max(0, docsMeta.skip - docsMeta.limit))}
                        disabled={docsMeta.skip <= 0}
                        className="rounded-full border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
                        data-testid="docs-prev">Prev</button>
                <button onClick={() => loadDocuments(docsMeta.skip + docsMeta.limit)}
                        disabled={!docsMeta.has_more}
                        className="rounded-full border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
                        data-testid="docs-next">Next</button>
              </div>
            </div>
          </div>
        )}

        {tab === "activity" && (
          <div data-testid="admin-activity-tab">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="kicker !text-slate-500 flex items-center gap-2">
                <Activity className="h-3.5 w-3.5" /> Admin audit trail
              </div>
              <button onClick={() => loadAuditLogs(0)} className="text-xs text-[#0A192F] hover:underline" data-testid="audit-refresh">
                <RefreshCw className="h-3 w-3 inline -mt-0.5 mr-1" /> Refresh
              </button>
            </div>
            <div className="mb-4 flex items-end gap-2 flex-wrap">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Type</label>
                <select value={auditType} onChange={(e) => setAuditType(e.target.value)}
                        className="mt-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                        data-testid="audit-filter-type">
                  <option value="">All</option>
                  {auditMeta.types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Since</label>
                <input type="date" value={auditSince} onChange={(e) => setAuditSince(e.target.value)}
                       className="mt-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                       data-testid="audit-filter-since" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Until</label>
                <input type="date" value={auditUntil} onChange={(e) => setAuditUntil(e.target.value)}
                       className="mt-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                       data-testid="audit-filter-until" />
              </div>
              <button onClick={() => loadAuditLogs(0)}
                      className="btn-primary rounded-full px-4 py-1.5 text-sm"
                      data-testid="audit-apply-filters">Apply</button>
              {(auditType || auditSince || auditUntil) && (
                <button onClick={() => { setAuditType(""); setAuditSince(""); setAuditUntil(""); setTimeout(() => loadAuditLogs(0), 0); }}
                        className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 text-slate-600"
                        data-testid="audit-clear-filters">Clear</button>
              )}
            </div>
            <div className="space-y-2" data-testid="audit-timeline">
              {auditLogs.map((log) => {
                const ts = log.created_at || log.timestamp || log._ts || "";
                const typeColor = {
                  "admin.rechunk_document": "bg-[#8B1E3F]/10 text-[#8B1E3F] border-[#8B1E3F]/30",
                  "admin.reembed_corpus":   "bg-[#0A192F]/10 text-[#0A192F] border-[#0A192F]/30",
                  "contact_sales":          "bg-amber-50 text-amber-800 border-amber-200",
                }[log.type] || "bg-slate-100 text-slate-600 border-slate-200";
                return (
                  <div key={log.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-start gap-3 flex-wrap"
                       data-testid={`audit-row-${log.id}`}>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${typeColor}`}>
                      {log.type}
                    </span>
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-xs text-slate-700">
                        {log.admin_email && <span className="font-medium">{log.admin_email}</span>}
                        {log.document_id && <> · doc <span className="font-mono">{log.document_id.slice(0, 8)}</span> <span className="text-slate-500">({log.filename})</span> {log.rehydrated_from_storage && <span className="text-emerald-600">· rehydrated</span>}</>}
                        {log.type === "admin.reembed_corpus" && <> · embedded <span className="font-mono">{log.embedded}/{log.total}</span> in <span className="font-mono">{log.batches}</span> batch(es) of ≤{log.batch_size}</>}
                        {log.type === "contact_sales" && <> · {log.email} <span className="text-slate-500">({log.firm || "—"})</span></>}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400 font-mono">{ts ? new Date(ts).toLocaleString() : "—"}</span>
                  </div>
                );
              })}
              {auditLogs.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">No audit log entries match your filters</div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500" data-testid="audit-pagination">
              <span>
                Showing {auditLogs.length === 0 ? 0 : auditMeta.skip + 1}–{auditMeta.skip + auditLogs.length} of {auditMeta.total}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => loadAuditLogs(Math.max(0, auditMeta.skip - auditMeta.limit))}
                        disabled={auditMeta.skip <= 0}
                        className="rounded-full border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
                        data-testid="audit-prev">Prev</button>
                <button onClick={() => loadAuditLogs(auditMeta.skip + auditMeta.limit)}
                        disabled={!auditMeta.has_more}
                        className="rounded-full border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
                        data-testid="audit-next">Next</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
